import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';

import { Icon } from '@sdb/planning-ui';
import { ControlledTextField } from 'components/shared';
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import type {
  OpeningScheduleFormProps,
  OpeningScheduleFormValues
} from '../../OpeningHours.types';
import { useSaveOpeningSchedule } from '../../hooks/useSaveOpeningSchedule';
import {
  emptyBlock,
  toFormValues,
  toRequest
} from '../../utils/openingScheduleForm';
import OpeningBlockFields from '../OpeningBlockFields/OpeningBlockFields';
import { schema } from './OpeningScheduleForm.schema';

/**
 * An opening schedule with its lines, saved as a whole: a half-saved schedule is worse than a
 * rejected form. One line on screen is one record: weekdays, one time slot or closed, and the day
 * types it applies to.
 */
const OpeningScheduleForm = ({
  planUnitId,
  rowData,
  template,
  handleClose
}: OpeningScheduleFormProps) => {
  'use memo';
  const { t } = useTranslation('common', {
    keyPrefix: 'planUnitHome.openingHours'
  });

  const methods = useForm<OpeningScheduleFormValues>({
    defaultValues: toFormValues(rowData ?? template),
    resolver: yupResolver(schema),
    mode: 'onSubmit'
  });
  const { control, handleSubmit } = methods;
  const {
    fields: blocks,
    append,
    remove
  } = useFieldArray({ control, name: 'blocks' });

  const { save, isSaving } = useSaveOpeningSchedule(
    planUnitId,
    rowData?.openingScheduleId,
    handleClose
  );

  const onSubmit = (values: OpeningScheduleFormValues) =>
    save(toRequest(values));

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section title={t('schedule')}>
        <ControlledTextField
          control={control}
          name="name"
          label={t('name')}
          required
          fullWidth
        />
        <ControlledValidFromValidTo
          control={control}
          validFromFieldName="validFrom"
          validToFieldName="validTo"
          validFromRequired
        />
        <ControlledTextField
          control={control}
          name="description"
          label={t('description')}
          fullWidth
        />
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('blocks')} fullWidth>
        {blocks.map((block, blockIndex) => (
          <OpeningBlockFields
            key={block.id}
            blockIndex={blockIndex}
            canRemove={blocks.length > 1}
            onRemove={() => remove(blockIndex)}
          />
        ))}
        <Box>
          <Button
            variant="text"
            startIcon={<Icon type="plus" />}
            onClick={() => append(emptyBlock())}
          >
            {t('addBlock')}
          </Button>
        </Box>
      </MultiStepForm.Section>

      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton loading={isSaving} type="submit" variant="contained">
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default OpeningScheduleForm;
